import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { Add, ViewColumn, Delete, MoreVert, FilterList, Search } from '@mui/icons-material';
import { Box, Button, Grid, Paper, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Menu, Chip, InputAdornment, Checkbox, FormControlLabel, Alert, Tabs, Tab } from '@mui/material';
import { AdminPageFrame } from 'components/AdminPageFrame';
import { MobileTabSelect } from 'components/MobileTabSelect';
import { Lead, LeadStatus, StatusHistory } from '@otw/models/leads/lead';
import { useLeads } from 'hooks/admin/useLeads';
import { useIsMobile } from 'hooks/screen/useIsMobile';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LeadList } from './components/cards';
import { LeadDetailDialog, LeadFormDialog } from './components/dialogs';
import { generateRandomColor } from 'core/utils/colorUtils';
import { LIGHT_STATUS } from 'core/theme.tokens';
import { collection, doc, getDoc, getDocs, setDoc, query, where, deleteDoc } from 'firebase/firestore';
import { db } from 'config/firebase';
import { useAuth } from 'contexts/AuthContext';
import { salesReps } from '@/constants';
import { SelectChangeEvent } from '@mui/material/Select';
import { usePermissions } from 'hooks/auth/usePermissions';
import PermissionDenied from 'components/PermissionDenied';
import { extractUniqueTruckOptions } from './util';
import type { InitialClientForLead } from './components/dialogs';
import {
  CompaniesTab,
  ContactsTab,
  CampaignsTab,
  LeadsTab,
  TasksTab,
} from './components/tabs';
import { CrmPageChromeProvider, CrmPageActionsOutlet } from './components/CrmPageChrome';
import { usePickupLocations } from 'hooks/inventory/usePickupLocations';
import { useInventory } from 'hooks/inventory/useInventory';
import { useUsers } from 'hooks/useUsers';
import { useContactCompanies } from 'hooks/contacts/useContactCompanies';
import type { OutOfScopeLeadMatch } from './outOfScopeLeadMatch';
import {
  canMoveLeadToClosed,
} from './util/leadDropoffValidation';
import { leadIdFromLocation } from './util/leadDeepLink';

type CrmTabValue = 'contacts' | 'companies' | 'leads' | 'tasks' | 'campaigns';

const CRM_TAB_META: Record<CrmTabValue, { label: string; description: string }> = {
  contacts: {
    label: 'Contacts',
    description:
      'The centralized view of every person we talk to — prospects before a lead, opportunities with an open lead, and customers who have closed.',
  },
  companies: {
    label: 'Companies',
    description:
      'Group every contact by their organization — track business addresses, dropoff addresses, and who plays the owner or manager role.',
  },
  leads: {
    label: 'Leads',
    description:
      'Track every active opportunity from first contact through closing. Drag leads between stages to keep your pipeline clean and your team in sync.',
  },
  tasks: {
    label: 'Tasks',
    description:
      'Plan personal touches with your customers — birthdays, purchase anniversaries, lost-lead re-engagement, and follow-ups. Switch between day, week, and month views to focus.',
  },
  campaigns: {
    label: 'Campaigns',
    description:
      'Build an audience from live CRM rules, write the email once, then send now or schedule it. Pause, resume, or cancel before it finishes. Marketing sends never touch sales follow-up dates.',
  },
};

interface LocalColumn {
  id: string;
  leads: Lead[];
  leadOrder: string[];
  name: string;
  color: string;
}

interface CustomColumn {
  id: string;
  name: string;
  color: string;
  position: number;
  createdBy: string;
}

interface LocalState {
  columns: { [key: string]: LocalColumn };
  columnOrder: string[];
}

const BORDER_COLORS = {
  new: LIGHT_STATUS.new.fg,
  inProgress: LIGHT_STATUS.inProgress.fg,
  waitingFinance: LIGHT_STATUS.waiting.fg,
  closed: LIGHT_STATUS.success.fg,
  lost: LIGHT_STATUS.danger.fg,
};

// User-friendly status names
const STATUS_NAMES = {
  new: 'New Leads',
  inProgress: 'In Progress',
  waitingFinance: 'Waiting Finance',
  closed: 'Closed',
  lost: 'Lost'
};

// Default columns that cannot be deleted
const DEFAULT_COLUMNS = ['new', 'inProgress', 'waitingFinance', 'closed', 'lost'];

const getAssignedRepNameFromId = (
  repId: string | number | undefined,
  reps: typeof salesReps
): string => {
  if (repId === undefined || repId === null) return 'Unassigned';
  const rep = reps.find((candidate) => String(candidate.id) === String(repId));
  return rep ? `${rep.first_name} ${rep.last_name}` : 'Unknown';
};

const doesLeadMatchSearchQuery = (
  lead: Lead,
  query: string,
  reps: typeof salesReps
): boolean => {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return true;
  const matchesTruckField = (value: unknown): boolean => {
    if (Array.isArray(value)) {
      return value.some((entry) => String(entry).toLowerCase().includes(normalizedQuery));
    }
    if (typeof value === 'string') {
      return value.toLowerCase().includes(normalizedQuery);
    }
    return false;
  };

  if (lead.leadNumber?.toLowerCase().includes(normalizedQuery)) return true;
  if (lead.customerName?.toLowerCase().includes(normalizedQuery)) return true;
  if (lead.company?.toLowerCase().includes(normalizedQuery)) return true;

  if (
    lead.assignedRepId &&
    reps.some(
      (rep) =>
        String(rep.id) === String(lead.assignedRepId) &&
        `${rep.first_name} ${rep.last_name}`.toLowerCase().includes(normalizedQuery)
    )
  ) {
    return true;
  }

  if (!lead.truckRequest?.types) return false;

  return lead.truckRequest.types.some((truckType) => {
    const matchesType = matchesTruckField(truckType.type);
    const matchesMake = matchesTruckField(truckType.make);
    const matchesModel = matchesTruckField(truckType.model);
    const matchesSize = matchesTruckField(truckType.size);

    return matchesType || matchesMake || matchesModel || matchesSize;
  });
};

// Create initial state for the board
const createInitialState = (
  leads: Lead[],
  customColumns: CustomColumn[] = [],
  statusColors: typeof BORDER_COLORS = BORDER_COLORS,
): LocalState => {
  // Group leads by status
  const leadsByStatus: Record<string, Lead[]> = {};
  
  // Initialize with default columns
  const columns: Record<string, LocalColumn> = {
    new: {
      id: 'new',
      leads: [],
      leadOrder: [],
      name: STATUS_NAMES.new,
      color: statusColors.new
    },
    inProgress: {
      id: 'inProgress',
      leads: [],
      leadOrder: [],
      name: STATUS_NAMES.inProgress,
      color: statusColors.inProgress
    },
    waitingFinance: {
      id: 'waitingFinance',
      leads: [],
      leadOrder: [],
      name: STATUS_NAMES.waitingFinance,
      color: statusColors.waitingFinance
    },
    closed: {
      id: 'closed',
      leads: [],
      leadOrder: [],
      name: STATUS_NAMES.closed,
      color: statusColors.closed
    },
    lost: {
      id: 'lost',
      leads: [],
      leadOrder: [],
      name: STATUS_NAMES.lost,
      color: statusColors.lost
    }
  };
  
  // Add custom columns
  customColumns.forEach(customColumn => {
    columns[customColumn.id] = {
      id: customColumn.id,
      leads: [],
      leadOrder: [],
      name: customColumn.name,
      color: customColumn.color
    };
  });
  
  // Add leads to their respective columns
  leads.forEach(lead => {
    const status = lead.status;
    if (!status) {
      // Handle null status - add to "new" as a default
      columns["new"].leads.push(lead);
      columns["new"].leadOrder.push(lead.id);
      return;
    }
    
    if (!columns[status]) {
      // If status doesn't exist as a column, create it
      columns[status] = {
        id: status,
        leads: [],
        leadOrder: [],
        name: status.charAt(0).toUpperCase() + status.slice(1),
        color: generateRandomColor()
      };
    }
    
    columns[status].leads.push(lead);
    columns[status].leadOrder.push(lead.id);
  });
  
  // Create column order based on default columns and custom columns
  const columnOrder = [...DEFAULT_COLUMNS];
  
  // Add custom columns in their specified positions
  customColumns.sort((a, b) => a.position - b.position);
  customColumns.forEach(customColumn => {
    // Make sure we don't add duplicates
    if (!columnOrder.includes(customColumn.id)) {
      // Insert at the specified position, or at the end if position is invalid
      const position = Math.min(customColumn.position, columnOrder.length);
      columnOrder.splice(position, 0, customColumn.id);
    }
  });
  
  // Add any other columns that might exist in the data but aren't in the default or custom columns
  Object.keys(columns).forEach(columnId => {
    if (!columnOrder.includes(columnId)) {
      columnOrder.push(columnId);
    }
  });
  
  return {
    columns,
    columnOrder
  };
};

const LeadManagement: React.FC = () => (
  <CrmPageChromeProvider>
    <LeadManagementContent />
  </CrmPageChromeProvider>
);

const LeadManagementContent: React.FC = () => {
  const { leads, isLoading, updateLead, deleteLead } = useLeads();
  const { pickupLocations } = usePickupLocations();
  const { vehicles } = useInventory('master_inventory');
  const { users } = useUsers();
  const { companies: contactCompanies } = useContactCompanies();
  const { userInfo } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Add permission check
  const { checkPermission, leadManagementScope } = usePermissions();
  const hasCreatePermission = checkPermission('lead_management', 'create');
  
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [state, setState] = useState<LocalState>({ columns: {}, columnOrder: [] });
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const isMobile = useIsMobile();
  const theme = useTheme();
  const statusColors = {
    new: theme.custom.status.new.fg,
    inProgress: theme.custom.status.inProgress.fg,
    waitingFinance: theme.custom.status.waiting.fg,
    closed: theme.custom.status.success.fg,
    lost: theme.custom.status.danger.fg,
  };
  const [isSuggestedTrucksOpen, setIsSuggestedTrucksOpen] = useState(false);
  const [suggestedTrucksLead, setSuggestedTrucksLead] = useState<Lead | null>(null);
  const [isColumnDialogOpen, setIsColumnDialogOpen] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newColumnPosition, setNewColumnPosition] = useState(0);
  const [isDeleteColumnDialogOpen, setIsDeleteColumnDialogOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<string | null>(null);
  const [targetColumnForLeads, setTargetColumnForLeads] = useState<string>('');
  const [isLoadingColumns, setIsLoadingColumns] = useState(true);
  
  // Filter states
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filterCompany, setFilterCompany] = useState<string>('');
  const [filterYear, setFilterYear] = useState<string>('');
  const [filterMonth, setFilterMonth] = useState<string>('');
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[] | null>(null);
  const [activeFilters, setActiveFilters] = useState<{
    company?: string, 
    month?: string, 
    year?: string,
    assignedRep?: string,
    truckType?: string,
    truckMake?: string,
    truckModel?: string,
    truckSize?: string,
    truckYear?: string
  }>({});

  // Add state for delete confirmation dialog
  const [isDeleteLeadDialogOpen, setIsDeleteLeadDialogOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [isMissingDropoffDialogOpen, setIsMissingDropoffDialogOpen] = useState(false);

  // Add new state for assigned rep filter
  const [filterAssignedRep, setFilterAssignedRep] = useState<string>('');

  // Add search state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [outOfScopeLeadMatches, setOutOfScopeLeadMatches] = useState<OutOfScopeLeadMatch[]>([]);

  // Add truck filter states
  const [filterTruckType, setFilterTruckType] = useState<string>('');
  const [filterTruckMake, setFilterTruckMake] = useState<string>('');
  const [filterTruckModel, setFilterTruckModel] = useState<string>('');
  const [filterTruckSize, setFilterTruckSize] = useState<string>('');
  const [filterTruckYear, setFilterTruckYear] = useState<string>('');
  const [availableTruckModels, setAvailableTruckModels] = useState<string[]>([]);
  const [availableTruckSizes, setAvailableTruckSizes] = useState<string[]>([]);
  const [availableTruckTypes, setAvailableTruckTypes] = useState<string[]>([]);
  const [availableTruckMakes, setAvailableTruckMakes] = useState<string[]>([]);
  const [availableTruckYears, setAvailableTruckYears] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<CrmTabValue>('contacts');

  // Hidden board columns (column ids to hide; toggled in filter dialog)
  const [hiddenColumnIds, setHiddenColumnIds] = useState<string[]>([]);
  const [showHiddenLeads, setShowHiddenLeads] = useState(false);
  const [showOtherSalesReps, setShowOtherSalesReps] = useState(false);

  // When navigating from Create User with "Create lead" checked, open create lead dialog with client pre-selected
  const [initialClientForLead, setInitialClientForLead] = useState<InitialClientForLead | null>(null);

  const leadsForUserScope = React.useMemo(() => {
    if (!leads) return null;
    if (leadManagementScope === 'none') return [];
    if (leadManagementScope === 'all' && showOtherSalesReps) return leads;
    if (!userInfo?.id) return [];
    return leads.filter((lead) => String(lead.assignedRepId) === String(userInfo.id));
  }, [leads, userInfo?.id, leadManagementScope, showOtherSalesReps]);

  const leadsForBoard = React.useMemo(() => {
    if (!leadsForUserScope) return null;
    if (showHiddenLeads) return leadsForUserScope;
    return leadsForUserScope.filter((lead) => lead.status !== 'closed' && lead.status !== 'lost');
  }, [leadsForUserScope, showHiddenLeads]);

  // Helper function to filter leads by truck property
  const filterLeadsByTruckProperty = useCallback((
    leads: Lead[], 
    filterValue: string, 
    propertyName: 'type' | 'make' | 'model' | 'size' | 'minYear'
  ): Lead[] => {
    if (!filterValue) return leads;
    
    return leads.filter(lead => {
      if (!lead.truckRequest?.types) return false;
      
      return lead.truckRequest.types.some(truckType => {
        const propertyValue = truckType[propertyName];
        
        if (propertyName === 'minYear') {
          // Special handling for year comparison
          return propertyValue && propertyValue.toString() === filterValue;
        }
        
        // Handle both array and string types for other properties
        if (Array.isArray(propertyValue)) {
          return (propertyValue as string[]).includes(filterValue);
        } else if (typeof propertyValue === 'string') {
          return (propertyValue as string) === filterValue;
        }
        
        return false;
      });
    });
  }, []);

  // Generate years for filter (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => (currentYear - i).toString());
  
  // Month names for filter
  const months = [
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' }
  ];

  // Load custom columns from Firestore
  const loadCustomColumns = async () => {
    try {
      setIsLoadingColumns(true);
      const columnsCollection = collection(db, 'leadColumns');
      const columnsQuery = query(columnsCollection);
      const querySnapshot = await getDocs(columnsQuery);
      
      const loadedColumns: CustomColumn[] = [];
      querySnapshot.forEach(doc => {
        loadedColumns.push(doc.data() as CustomColumn);
      });
      
      setCustomColumns(loadedColumns);
      return loadedColumns;
    } catch (error) {
      console.error('Error loading custom columns:', error);
      return [];
    } finally {
      setIsLoadingColumns(false);
    }
  };

  // Save a custom column to Firestore
  const saveCustomColumn = async (column: CustomColumn) => {
    try {
      const columnRef = doc(db, 'leadColumns', column.id);
      await setDoc(columnRef, column);
      return true;
    } catch (error) {
      console.error('Error saving custom column:', error);
      return false;
    }
  };

  // Delete a custom column from Firestore
  const deleteCustomColumn = async (columnId: string) => {
    try {
      const columnRef = doc(db, 'leadColumns', columnId);
      await deleteDoc(columnRef);
      return true;
    } catch (error) {
      console.error('Error deleting custom column:', error);
      return false;
    }
  };

  // Extract unique companies from leads
  useEffect(() => {
    if (leadsForBoard) {
      const companies = Array.from(new Set(
        leadsForBoard
          .map(lead => lead.company)
          .filter(company => company && company.trim() !== '') as string[]
      )).sort();
      
      setAvailableCompanies(companies);
    }
  }, [leadsForBoard]);

  // When navigated from Create User with "Create lead" checked, open create lead dialog with client pre-selected
  useEffect(() => {
    const locState = location.state as { createLeadWithClient?: InitialClientForLead } | null;
    if (locState?.createLeadWithClient) {
      setInitialClientForLead(locState.createLeadWithClient);
      setSelectedLead(null);
      setIsCreatingLead(true);
      setIsDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Load leads and custom columns when component mounts
  useEffect(() => {
    const initializeBoard = async () => {
      const loadedColumns = await loadCustomColumns();
      const leadsToUse = filteredLeads || leadsForBoard;
      if (leadsToUse) {
        setState(createInitialState(leadsToUse, loadedColumns, statusColors));
      }
    };
    
    initializeBoard();
  }, [leadsForBoard, filteredLeads, theme]);

  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // If there's no destination or the item was dropped back in its original position
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }

    // Find the lead that was dragged
    const draggedLead = leads?.find(lead => lead.id === draggableId);
    if (!draggedLead) return;

    if (
      destination.droppableId === 'closed' &&
      source.droppableId !== 'closed' &&
      !canMoveLeadToClosed(
        draggedLead,
        pickupLocations,
        contactCompanies,
      )
    ) {
      setIsMissingDropoffDialogOpen(true);
      return;
    }

    try {
      // Update local state first for immediate UI feedback
      const newState = { ...state };
      
      // Remove from source column
      const sourceColumn = { ...newState.columns[source.droppableId] };
      sourceColumn.leads = [...sourceColumn.leads];
      sourceColumn.leadOrder = [...sourceColumn.leadOrder];
      sourceColumn.leads.splice(source.index, 1);
      sourceColumn.leadOrder.splice(source.index, 1);
      
      // Add to destination column
      const destColumn = { ...newState.columns[destination.droppableId] };
      destColumn.leads = [...destColumn.leads];
      destColumn.leadOrder = [...destColumn.leadOrder];
      
      // Create status change note
      const sourceColumnName = state.columns[source.droppableId].name;
      const destColumnName = state.columns[destination.droppableId].name;
      const statusChangeNote = `Moved from ${sourceColumnName} to ${destColumnName}`;
      
      // Create a copy of the lead with updated status
      const updatedLead = { 
        ...draggedLead, 
        status: destination.droppableId as LeadStatus,
        prevStatus: draggedLead.status
      };
      
      // Insert at the destination index
      destColumn.leads.splice(destination.index, 0, updatedLead);
      destColumn.leadOrder.splice(destination.index, 0, updatedLead.id);
      
      // Update the columns in state
      newState.columns[source.droppableId] = sourceColumn;
      newState.columns[destination.droppableId] = destColumn;
      
      // Update state
      setState(newState);
      
      // Update the lead in the database
      await updateLead({
        id: draggableId,
        updates: {
          status: destination.droppableId as LeadStatus,
          prevStatus: draggedLead.status
        },
        statusChangeNote: statusChangeNote
      });
      
    } catch (error) {
      console.error('Error updating lead status:', error);
      // Revert to original state on error
    }
  };

  const handleLeadSelect = useCallback((lead: Lead) => {
    setSelectedLead(lead);
    setIsCreatingLead(false);
    setIsDialogOpen(true);
  }, []);

  const handleCreateLead = useCallback(() => {
    setSelectedLead(null);
    setIsCreatingLead(true);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedLead(null);
    setInitialClientForLead(null);
  }, []);

  const handleViewDetails = useCallback((lead: Lead) => {
    setSelectedLead(lead);
    setIsDetailDialogOpen(true);
  }, []);

  const handleCloseDetailDialog = useCallback(() => {
    setIsDetailDialogOpen(false);
    setSelectedLead(null);
  }, []);

  const handleEditFromDetails = (lead: Lead) => {
    setSelectedLead(lead);
    setIsCreatingLead(false);
    setIsDetailDialogOpen(false);
    setIsDialogOpen(true);
  };

  const handleViewSuggestedTrucks = useCallback((lead: Lead) => {
    setSuggestedTrucksLead(lead);
    setIsSuggestedTrucksOpen(true);
  }, []);

  const handleOpenColumnDialog = useCallback(() => {
    setNewColumnTitle('');
    setNewColumnPosition(state.columnOrder.length); // Default to last position
    setIsColumnDialogOpen(true);
  }, [state.columnOrder.length]);

  const handleCreateColumn = async () => {
    if (!newColumnTitle.trim() || !userInfo?.id) return;
    
    // Generate a unique ID for the column
    const columnId = `custom_${Date.now()}`;
    const color = generateRandomColor();
    
    // Create the custom column object
    const newCustomColumn: CustomColumn = {
      id: columnId,
      name: newColumnTitle.trim(),
      color,
      position: newColumnPosition,
      createdBy: userInfo.id
    };
    
    // Save to Firestore
    const saved = await saveCustomColumn(newCustomColumn);
    
    if (saved) {
      // Update local state
      setCustomColumns(prev => [...prev, newCustomColumn]);
      
      // Update the board state
      const newColumns = { ...state.columns };
      newColumns[columnId] = {
        id: columnId,
        name: newCustomColumn.name,
        color: newCustomColumn.color,
        leads: [],
        leadOrder: []
      };
      
      // Insert the new column at the specified position
      const newColumnOrder = [...state.columnOrder];
      newColumnOrder.splice(newColumnPosition, 0, columnId);
      
      setState({
        columns: newColumns,
        columnOrder: newColumnOrder
      });
    }
    
    // Reset form and close dialog
    setNewColumnTitle('');
    setNewColumnPosition(0);
    setIsColumnDialogOpen(false);
  };

  const handleDeleteColumn = (columnId: string) => {
    setColumnToDelete(columnId);
    // Set default target column (first column that's not the one being deleted)
    const firstAvailableColumn = state.columnOrder.find(id => id !== columnId);
    if (firstAvailableColumn) {
      setTargetColumnForLeads(firstAvailableColumn);
    }
    setIsDeleteColumnDialogOpen(true);
  };

  const confirmDeleteColumn = async () => {
    if (!columnToDelete || !targetColumnForLeads) return;

    // Get the column being deleted and its leads
    const columnToRemove = state.columns[columnToDelete];
    const leadsToMove = columnToRemove.leads;

    // Delete from Firestore if it's a custom column
    if (!DEFAULT_COLUMNS.includes(columnToDelete)) {
      await deleteCustomColumn(columnToDelete);
      
      // Update local custom columns state
      setCustomColumns(prev => prev.filter(col => col.id !== columnToDelete));
    }

    // Update the state to remove the column
    const newColumnOrder = state.columnOrder.filter(id => id !== columnToDelete);
    const newColumns = { ...state.columns };
    delete newColumns[columnToDelete];

    // Move leads to the target column and update their status in the database
    if (leadsToMove.length > 0) {
      // Update leads in the database
      for (const lead of leadsToMove) {
        await updateLead({
          id: lead.id,
          updates: { status: targetColumnForLeads as LeadStatus },
          statusChangeNote: `Moved from deleted column: ${columnToRemove.name}`
        });
      }

      // Add the leads to the target column
      newColumns[targetColumnForLeads].leads = [
        ...newColumns[targetColumnForLeads].leads,
        ...leadsToMove
      ];
      newColumns[targetColumnForLeads].leadOrder = [
        ...newColumns[targetColumnForLeads].leadOrder,
        ...leadsToMove.map(lead => lead.id)
      ];
    }

    // Update the state
    setState({
      columns: newColumns,
      columnOrder: newColumnOrder
    });

    // Close the dialog
    setIsDeleteColumnDialogOpen(false);
    setColumnToDelete(null);
    setTargetColumnForLeads('');
  };

  // Visible board columns (exclude hidden)
  const visibleColumnOrder = React.useMemo(
    () => state.columnOrder.filter((id) => !hiddenColumnIds.includes(id)),
    [state.columnOrder, hiddenColumnIds]
  );

  const toggleColumnVisibility = useCallback((columnId: string) => {
    setHiddenColumnIds((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
    );
  }, []);

  // Handle opening the filter dialog
  const handleOpenFilterDialog = useCallback(() => {
    setIsFilterDialogOpen(true);
  }, []);

  // Apply filters to leads
  const applyFilters = useCallback(() => {
    if (!leadsForBoard) return;
    
    const newActiveFilters: {company?: string, month?: string, year?: string, assignedRep?: string, truckType?: string, truckMake?: string, truckModel?: string, truckSize?: string, truckYear?: string} = {};
    let filtered = [...leadsForBoard];
    
    // Apply company filter if selected
    if (filterCompany) {
      filtered = filtered.filter(lead => lead.company === filterCompany);
      newActiveFilters.company = filterCompany;
    }
    
    // Apply year and month filters if selected
    if (filterYear) {
      newActiveFilters.year = filterYear;
      
      filtered = filtered.filter(lead => {
        if (!lead.createdAt) return false;
        
        const leadDate = new Date(lead.createdAt);
        return leadDate.getFullYear().toString() === filterYear;
      });
      
      // Apply month filter only if year is selected
      if (filterMonth) {
        newActiveFilters.month = filterMonth;
        
        filtered = filtered.filter(lead => {
          if (!lead.createdAt) return false;
          
          const leadDate = new Date(lead.createdAt);
          return leadDate.getMonth().toString() === filterMonth;
        });
      }
    }
    
    // Apply assigned rep filter if selected
    if (filterAssignedRep) {
      filtered = filtered.filter(lead => {
        // Convert both to the same type for comparison
        // Some IDs might be strings and some might be numbers
        return String(lead.assignedRepId) === String(filterAssignedRep);
      });
      newActiveFilters.assignedRep = filterAssignedRep;
    }
    
    // Apply truck filters
    if (filterTruckType) {
      filtered = filterLeadsByTruckProperty(filtered, filterTruckType, 'type');
      newActiveFilters.truckType = filterTruckType;
    }
    
    if (filterTruckMake) {
      filtered = filterLeadsByTruckProperty(filtered, filterTruckMake, 'make');
      newActiveFilters.truckMake = filterTruckMake;
    }
    
    if (filterTruckModel) {
      filtered = filterLeadsByTruckProperty(filtered, filterTruckModel, 'model');
      newActiveFilters.truckModel = filterTruckModel;
    }
    
    if (filterTruckSize) {
      filtered = filterLeadsByTruckProperty(filtered, filterTruckSize, 'size');
      newActiveFilters.truckSize = filterTruckSize;
    }
    
    if (filterTruckYear) {
      filtered = filterLeadsByTruckProperty(filtered, filterTruckYear, 'minYear');
      newActiveFilters.truckYear = filterTruckYear;
    }
    
    // Update filtered leads and active filters
    setFilteredLeads(filtered.length === leadsForBoard.length ? null : filtered);
    setActiveFilters(newActiveFilters);
    setIsFilterDialogOpen(false);
  }, [leadsForBoard, filterCompany, filterYear, filterMonth, filterAssignedRep, filterTruckType, filterTruckMake, filterTruckModel, filterTruckSize, filterTruckYear, filterLeadsByTruckProperty]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilterCompany('');
    setFilterYear('');
    setFilterMonth('');
    setFilterAssignedRep('');
    setFilterTruckType('');
    setFilterTruckMake('');
    setFilterTruckModel('');
    setFilterTruckSize('');
    setFilterTruckYear('');
    setFilteredLeads(null);
    setActiveFilters({});
    setIsFilterDialogOpen(false);
  }, []);

  // Remove a specific filter
  const removeFilter = useCallback((filterType: 'company' | 'month' | 'year' | 'assignedRep' | 'truckType' | 'truckMake' | 'truckModel' | 'truckSize' | 'truckYear') => {
    const newActiveFilters = {...activeFilters};
    
    if (filterType === 'company') {
      delete newActiveFilters.company;
      setFilterCompany('');
    } else if (filterType === 'year') {
      delete newActiveFilters.year;
      delete newActiveFilters.month; // Also remove month when year is removed
      setFilterYear('');
      setFilterMonth('');
    } else if (filterType === 'month') {
      delete newActiveFilters.month;
      setFilterMonth('');
    } else if (filterType === 'assignedRep') {
      delete newActiveFilters.assignedRep;
      setFilterAssignedRep('');
    } else if (filterType === 'truckType') {
      delete newActiveFilters.truckType;
      setFilterTruckType('');
    } else if (filterType === 'truckMake') {
      delete newActiveFilters.truckMake;
      setFilterTruckMake('');
    } else if (filterType === 'truckModel') {
      delete newActiveFilters.truckModel;
      setFilterTruckModel('');
    } else if (filterType === 'truckSize') {
      delete newActiveFilters.truckSize;
      setFilterTruckSize('');
    } else if (filterType === 'truckYear') {
      delete newActiveFilters.truckYear;
      setFilterTruckYear('');
    }
    
    if (Object.keys(newActiveFilters).length === 0) {
      setFilteredLeads(null);
      setActiveFilters({});
    } else {
      setActiveFilters(newActiveFilters);
      // Re-apply remaining filters
      let filtered = [...(leadsForBoard || [])];
      
      if (newActiveFilters.company) {
        filtered = filtered.filter(lead => lead.company === newActiveFilters.company);
      }
      
      if (newActiveFilters.year) {
        filtered = filtered.filter(lead => {
          if (!lead.createdAt) return false;
          
          const leadDate = new Date(lead.createdAt);
          return leadDate.getFullYear().toString() === newActiveFilters.year;
        });
        
        if (newActiveFilters.month) {
          filtered = filtered.filter(lead => {
            if (!lead.createdAt) return false;
            
            const leadDate = new Date(lead.createdAt);
            return leadDate.getMonth().toString() === newActiveFilters.month;
          });
        }
      }
      
      if (newActiveFilters.assignedRep) {
        filtered = filtered.filter(lead => {
          // Convert both to the same type for comparison
          return String(lead.assignedRepId) === String(newActiveFilters.assignedRep);
        });
      }
      
      if (newActiveFilters.truckType) {
        filtered = filterLeadsByTruckProperty(filtered, newActiveFilters.truckType, 'type');
      }
      
      if (newActiveFilters.truckMake) {
        filtered = filterLeadsByTruckProperty(filtered, newActiveFilters.truckMake, 'make');
      }
      
      if (newActiveFilters.truckModel) {
        filtered = filterLeadsByTruckProperty(filtered, newActiveFilters.truckModel, 'model');
      }
      
      if (newActiveFilters.truckSize) {
        filtered = filterLeadsByTruckProperty(filtered, newActiveFilters.truckSize, 'size');
      }
      
      if (newActiveFilters.truckYear) {
        filtered = filterLeadsByTruckProperty(filtered, newActiveFilters.truckYear, 'minYear');
      }
      
      setFilteredLeads(filtered);
    }
  }, [activeFilters, leadsForBoard, filterLeadsByTruckProperty]);

  // Handle year change in filter
  const handleYearChange = (event: SelectChangeEvent) => {
    const year = event.target.value;
    setFilterYear(year);
    
    // Reset month if year is cleared
    if (!year) {
      setFilterMonth('');
    }
  };

  // Handle lead deletion
  const handleDeleteLead = useCallback((lead: Lead) => {
    setLeadToDelete(lead);
    setIsDeleteLeadDialogOpen(true);
  }, []);

  // Confirm lead deletion
  const confirmDeleteLead = async () => {
    if (!leadToDelete) return;
    
    try {
      // Remove from local state first for immediate UI feedback
      const newState = { ...state };
      const statusColumn = newState.columns[leadToDelete.status || 'new'];
      
      // Remove the lead from its column
      statusColumn.leads = statusColumn.leads.filter(lead => lead.id !== leadToDelete.id);
      statusColumn.leadOrder = statusColumn.leadOrder.filter(id => id !== leadToDelete.id);
      
      // Update the state
      setState(newState);
      
      // Delete the lead from the database using the deleteLead function
      await deleteLead(leadToDelete.id);
      
      // Close the dialog
      setIsDeleteLeadDialogOpen(false);
      setLeadToDelete(null);
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  // Helper function to get rep name from ID
  const getRepNameById = useCallback((repId: string | number) => {
    const rep = salesReps.find(rep => rep.id === repId);
    return rep ? `${rep.first_name} ${rep.last_name}` : 'Unknown';
  }, [salesReps]);

  // Update available truck options when leads change
  useEffect(() => {
    if (leadsForBoard) {
      const { makes, models, sizes, types, years } = extractUniqueTruckOptions(leadsForBoard);
      setAvailableTruckMakes(makes);
      setAvailableTruckModels(models);
      setAvailableTruckSizes(sizes);
      setAvailableTruckTypes(types);
      setAvailableTruckYears(years);
    }
  }, [leadsForBoard]);

  // Open a lead from global search. All search includes leads outside the
  // current board, so the lookup uses the full lead list, not the scoped board.
  useEffect(() => {
    const leadId = leadIdFromLocation(location.search, location.hash);
    if (!leadId || !leads) return;

    const lead = leads.find((item) => item.id === leadId);
    if (!lead) return;

    const assignedToCurrentUser =
      Boolean(userInfo?.id) && String(lead.assignedRepId) === String(userInfo?.id);
    if (leadManagementScope === 'all' && userInfo?.id && !assignedToCurrentUser) {
      setShowOtherSalesReps(true);
    }

    setActiveTab('leads');
    setSelectedLead(lead);
    setIsDetailDialogOpen(true);

    const params = new URLSearchParams(location.search);
    params.delete('openLead');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: '',
      },
      { replace: true },
    );
  }, [location.search, location.hash, location.pathname, leads, navigate, leadManagementScope, userInfo?.id]);

  // Add search functionality - now only triggered when explicitly requested
  useEffect(() => {
    if (!leadsForBoard) return;
    
    // If search query is empty and no other filters are active, show all leads
    if (!searchQuery.trim() && Object.keys(activeFilters).length === 0) {
      setFilteredLeads(null);
      return;
    }
    
    // Start with currently visible leads and then apply active filters.
    let filtered = [...leadsForBoard];
    if (activeFilters.company) {
      filtered = filtered.filter(lead => lead.company === activeFilters.company);
    }
    if (activeFilters.year) {
      filtered = filtered.filter(lead => {
        if (!lead.createdAt) return false;
        const leadDate = new Date(lead.createdAt);
        return leadDate.getFullYear().toString() === activeFilters.year;
      });
      if (activeFilters.month) {
        filtered = filtered.filter(lead => {
          if (!lead.createdAt) return false;
          const leadDate = new Date(lead.createdAt);
          return leadDate.getMonth().toString() === activeFilters.month;
        });
      }
    }
    if (activeFilters.assignedRep) {
      filtered = filtered.filter(lead => String(lead.assignedRepId) === String(activeFilters.assignedRep));
    }
    if (activeFilters.truckType) {
      filtered = filterLeadsByTruckProperty(filtered, activeFilters.truckType, 'type');
    }
    if (activeFilters.truckMake) {
      filtered = filterLeadsByTruckProperty(filtered, activeFilters.truckMake, 'make');
    }
    if (activeFilters.truckModel) {
      filtered = filterLeadsByTruckProperty(filtered, activeFilters.truckModel, 'model');
    }
    if (activeFilters.truckSize) {
      filtered = filterLeadsByTruckProperty(filtered, activeFilters.truckSize, 'size');
    }
    if (activeFilters.truckYear) {
      filtered = filterLeadsByTruckProperty(filtered, activeFilters.truckYear, 'minYear');
    }
    
    // If search query exists, filter by it
    if (searchQuery.trim()) {
      filtered = filtered.filter(lead => 
        doesLeadMatchSearchQuery(lead, searchQuery, salesReps)
      );
    }
    
    setFilteredLeads(filtered.length === leadsForBoard.length && Object.keys(activeFilters).length === 0 ? null : filtered);
  }, [searchQuery, leadsForBoard, activeFilters, filterLeadsByTruckProperty]);

  // For non-owners: indicate when a searched lead exists but is assigned to another rep.
  useEffect(() => {
    if (!searchQuery.trim()) {
      setOutOfScopeLeadMatches([]);
      return;
    }

    if (!leads || !userInfo?.id) {
      setOutOfScopeLeadMatches([]);
      return;
    }

    // Users who can already see every rep's leads have nothing "out of scope".
    if (leadManagementScope === 'all' && showOtherSalesReps) {
      setOutOfScopeLeadMatches([]);
      return;
    }

    const matchesAssignedToOtherRep: OutOfScopeLeadMatch[] = leads
      .filter((lead) => {
      const isAssignedToCurrentUser = String(lead.assignedRepId) === String(userInfo.id);
      if (isAssignedToCurrentUser) return false;
      return doesLeadMatchSearchQuery(lead, searchQuery, salesReps);
      })
      .map((lead) => ({
        id: lead.id,
        leadNumber: lead.leadNumber,
        customerName: lead.customerName,
        company: lead.company,
        assignedRepName: getAssignedRepNameFromId(lead.assignedRepId, salesReps),
        email: lead.email,
        phone: lead.phone,
        status: lead.status,
        location: lead.location,
        source: lead.source,
        vehicleInterest: lead.vehicleInterest,
        timeFrame: lead.timeFrame,
        financingStatus: lead.financingStatus,
        priority: lead.priority,
      }));

    setOutOfScopeLeadMatches(matchesAssignedToOtherRep);
  }, [searchQuery, leads, userInfo?.id, leadManagementScope, showOtherSalesReps]);
  
  // Handle search input change - just updates the input value, doesn't trigger search
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
  };
  
  // Execute search when Enter key is pressed
  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setSearchQuery(searchInputValue);
    }
  };
  
  // Execute search when search icon is clicked
  const handleSearchClick = () => {
    setSearchQuery(searchInputValue);
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchInputValue('');
  };

  // If user has no visibility scope, show the PermissionDenied component.
  if (leadManagementScope === 'none') {
    return (
      <PermissionDenied
        category="lead_management"
        action="view"
        description="You need permission to view the Lead Management page."
      />
    );
  }

  return (
    <AdminPageFrame
      title="Lead Management"
      section={CRM_TAB_META[activeTab].label}
      subtitle={CRM_TAB_META[activeTab].description}
      action={<CrmPageActionsOutlet />}
    >
      <Box sx={{ mb: 0.5 }}>
        <MobileTabSelect
          label="Section"
          ariaLabel="Lead management CRM tabs"
          value={activeTab}
          onChange={setActiveTab}
          tabs={[
            { value: 'contacts', label: 'Contacts' },
            { value: 'companies', label: 'Companies' },
            { value: 'leads', label: 'Leads' },
            { value: 'tasks', label: 'Tasks' },
            { value: 'campaigns', label: 'Campaigns' },
          ]}
          desktop={
            <Tabs
              value={activeTab}
              onChange={(_, value: CrmTabValue) => setActiveTab(value)}
              variant="standard"
              aria-label="Lead management CRM tabs"
            >
              <Tab value="contacts" label="Contacts" disableRipple />
              <Tab value="companies" label="Companies" disableRipple />
              <Tab value="leads" label="Leads" disableRipple />
              <Tab value="tasks" label="Tasks" disableRipple />
              <Tab value="campaigns" label="Campaigns" disableRipple />
            </Tabs>
          }
        />
      </Box>
      <LeadsTab
        isActive={activeTab === 'leads'}
        vm={{
          searchInputValue,
          handleSearchInputChange,
          handleSearchKeyDown,
          handleSearchClick,
          showHiddenLeads,
          setShowHiddenLeads,
          showOtherSalesReps,
          setShowOtherSalesReps,
          hasViewAllPermission: leadManagementScope === 'all',
          handleOpenFilterDialog,
          handleOpenColumnDialog,
          hasCreatePermission,
          handleCreateLead,
          activeFilters,
          searchQuery,
          clearSearch,
          removeFilter,
          months,
          getRepNameById,
          clearFilters,
          outOfScopeLeadMatches,
          onDragEnd,
          isMobile,
          visibleColumnOrder,
          state,
          defaultColumns: DEFAULT_COLUMNS,
          handleLeadSelect,
          handleViewDetails,
          handleViewSuggestedTrucks,
          handleDeleteColumn,
          handleDeleteLead,
          isDialogOpen,
          handleCloseDialog,
          selectedLead,
          isCreatingLead,
          initialClientForLead,
          isDetailDialogOpen,
          handleCloseDetailDialog,
          handleEditFromDetails,
          isColumnDialogOpen,
          setIsColumnDialogOpen,
          newColumnTitle,
          setNewColumnTitle,
          newColumnPosition,
          setNewColumnPosition,
          handleCreateColumn,
          isDeleteColumnDialogOpen,
          setIsDeleteColumnDialogOpen,
          columnToDelete,
          targetColumnForLeads,
          setTargetColumnForLeads,
          confirmDeleteColumn,
          isFilterDialogOpen,
          setIsFilterDialogOpen,
          hiddenColumnIds,
          toggleColumnVisibility,
          filterCompany,
          setFilterCompany,
          availableCompanies,
          filterAssignedRep,
          setFilterAssignedRep,
          filterYear,
          handleYearChange,
          years,
          filterMonth,
          setFilterMonth,
          filterTruckType,
          setFilterTruckType,
          availableTruckTypes,
          filterTruckMake,
          setFilterTruckMake,
          availableTruckMakes,
          filterTruckModel,
          setFilterTruckModel,
          availableTruckModels,
          filterTruckSize,
          setFilterTruckSize,
          availableTruckSizes,
          filterTruckYear,
          setFilterTruckYear,
          availableTruckYears,
          applyFilters,
          isDeleteLeadDialogOpen,
          setIsDeleteLeadDialogOpen,
          leadToDelete,
          confirmDeleteLead,
          isMissingDropoffDialogOpen,
          setIsMissingDropoffDialogOpen,
        }}
      />
      <ContactsTab isActive={activeTab === 'contacts'} />
      <CompaniesTab isActive={activeTab === 'companies'} />
      <TasksTab isActive={activeTab === 'tasks'} />
      <CampaignsTab isActive={activeTab === 'campaigns'} />
    </AdminPageFrame>
  );
};

export default memo(LeadManagement);
